// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
import TableLoading from 'components/atoms/TableLoading';
import { IconSearch } from 'components/atoms/icons/IconSearch';
import DOMPurify from 'dompurify';
import _debounce from 'lodash/debounce';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DEFAULT_PAGE } from 'util/ConstantValues';
import { ModalType } from 'util/Enums';
import styles from './AdminOperatorItemsTable.module.scss';
import { InputDataListField } from 'components/molecules/InputDataListField';
import { SelectField, SelectOption } from 'components/molecules/SelectField';
import { Form } from 'react-final-form';
import { CustomCheckBoxV2 } from 'components/atoms/CustomCheckboxV2';
import { useParams } from 'react-router';
import {
  addOperatorItemForWasteUnit,
  getWasteUnitsByOperatorId,
  WasteUnitsByOperatorIdItem,
  WasteUnitsByOperatorIdParams,
} from 'apis/operator_site/siteApi';
import { getCommonOperatorItems } from 'apis/commons';
import { toast } from 'react-toastify';

export interface OperatorCreateEditModalType {
  isShow: boolean;
  type: ModalType;
  id?: string;
}

type Props = {
  siteOptions: SelectOption[];
  onTotalUnsettingItemsChange?: (total: number) => void;
};

const AdminOperatorItemsTable = ({
  siteOptions,
  onTotalUnsettingItemsChange,
}: Props) => {
  const { t } = useTranslation();
  const PAGE_SIZE = 500;
  const [selectedItemIds, setSelectedItemIds] = useState<number[]>([]);
  const { operatorId } = useParams<{ operatorId: string }>();

  const [operatorItemOptions, setOperatorItemOptions] = useState<
    SelectOption[]
  >([]);

  const [items, setItems] = useState<WasteUnitsByOperatorIdItem[]>([]);

  const [totalItems, setTotalItems] = useState(0);

  const [query, setQuery] = useState<WasteUnitsByOperatorIdParams>({
    siteId: '',
    keyword: '',
    page: DEFAULT_PAGE,
    take: PAGE_SIZE,
  });

  const [selectedOperatorItem, setSelectedOperatorItem] = useState<number>(0);

  const fetchItems = () => {
    const shouldSelectNewItems =
      selectedItemIds.length > 0 && selectedItemIds.length === items.length;

    getWasteUnitsByOperatorId({
      operatorId,
      query,
      successCallback: (data) => {
        if (data) {
          setItems((prev) =>
            query.page === DEFAULT_PAGE
              ? data.wasteUnits
              : [...prev, ...data.wasteUnits]
          );

          if (query.page !== DEFAULT_PAGE && shouldSelectNewItems) {
            setSelectedItemIds((prev) => {
              const newItemIds = data.wasteUnits
                .map((item) => item.id)
                .filter((id) => !prev.includes(id));

              return [...prev, ...newItemIds];
            });
          }

          setTotalItems(data.total);
          onTotalUnsettingItemsChange?.(data.wasteUnitWithoutOperatorItemCount);
        }
      },
    });
  };

  const refreshItemsAfterApply = () => {
    getWasteUnitsByOperatorId({
      operatorId,
      query: {
        ...query,
        page: DEFAULT_PAGE,
        take: query.take * query.page,
      },
      successCallback: (data) => {
        if (data) {
          setItems(data.wasteUnits);
          setTotalItems(data.total);
          onTotalUnsettingItemsChange?.(data.wasteUnitWithoutOperatorItemCount);
        }
      },
    });
  };

  const handleSelectSiteId = (siteId: string | number) => {
    setSelectedItemIds([]);
    setQuery((prev) => ({
      ...prev,
      siteId,
      page: DEFAULT_PAGE,
    }));
  };

  const handleKeywordChange = (value: string) => {
    debounceFn(() => {
      setSelectedItemIds([]);
      setQuery((prev) => ({
        ...prev,
        keyword: value,
        page: DEFAULT_PAGE,
      }));
    });
  };

  const debounceFn = useCallback(
    _debounce((callback) => callback(), 1000),
    []
  );

  const handleApply = (callback?: () => void) => {
    addOperatorItemForWasteUnit({
      operatorId,
      operatorItemId: selectedOperatorItem,
      wasteUnitIds: selectedItemIds,
      successCallback: () => {
        toast.success(t('messages.M_011'));
        setSelectedItemIds([]);
        setSelectedOperatorItem(0);
        refreshItemsAfterApply();
        if (callback) {
          callback();
        }
      },
    });
  };

  useEffect(() => {
    getCommonOperatorItems({
      operatorId,
      successCallback: (data) => {
        if (data) {
          setOperatorItemOptions([
            { value: '', label: '-' },
            ...data.map((item) => ({ value: item.id, label: item.name })),
          ]);
        }
      },
    });
  }, [operatorId]);

  useEffect(() => {
    fetchItems();
  }, [query]);

  return (
    <div className={styles.tableList}>
      <div className={styles.titleTable}>
        <div
          className="count-list"
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(
              t('common.page', {
                page: totalItems,
              }) ?? ''
            ),
          }}
        />
      </div>

      <div className={styles.searchInput}>
        <Form
          onSubmit={() => {}}
          initialValues={{
            siteId: '',
          }}
          mutators={{
            changeField: ([field, value], state, { changeValue }) => {
              changeValue(state, field, () => value);
            },
          }}
        >
          {(props) => {
            return (
              <form onSubmit={props.handleSubmit} className={styles.formInput}>
                <div className={styles.inputFrame}>
                  <div className={styles.searchWrapper}>
                    <div className={styles.selectOperator}>
                      <SelectField
                        name={'siteId'}
                        options={siteOptions}
                        onChange={handleSelectSiteId}
                        placeholder={t(
                          'setting_operator_name.select_site_placeholder'
                        )}
                        showPlaceholderOption
                      />
                    </div>

                    <div className={styles.inputSearch}>
                      <InputDataListField
                        handleChange={handleKeywordChange}
                        name="inputSearch"
                        required
                        placeholder={t(
                          'setting_operator_name.keyword_placeholder'
                        )}
                      />
                      <div className={styles.iconSearch}>
                        <IconSearch />
                      </div>
                    </div>
                  </div>
                  <div className={styles.applyWrapper}>
                    <div className={styles.selectOperator}>
                      <SelectField
                        name={'operatorItem'}
                        options={operatorItemOptions}
                        onChange={(value) =>
                          setSelectedOperatorItem(Number(value))
                        }
                        placeholder={t(
                          'setting_operator_name.select_operator_item_placeholder'
                        )}
                        showPlaceholderOption
                      />
                    </div>
                    <button
                      className={styles.applyButton}
                      type="button"
                      disabled={selectedItemIds.length === 0}
                      onClick={() =>
                        handleApply(() => {
                          props.form.mutators.changeField('operatorItem', '');
                        })
                      }
                    >
                      {t('setting_operator_name.apply_btn')}
                    </button>
                  </div>
                </div>
              </form>
            );
          }}
        </Form>
      </div>

      <div className={`scroll-wrapper ${styles.scrollWrapper}`}>
        <table id={styles.customizeItemTable}>
          <thead>
            <tr>
              <th className={styles.checkbox}>
                <CustomCheckBoxV2
                  id="check-all"
                  checked={
                    items.length > 0 && selectedItemIds.length === items.length
                  }
                  onChange={() => {
                    if (selectedItemIds.length === items.length) {
                      setSelectedItemIds([]);
                    } else {
                      setSelectedItemIds(items.map((item) => item.id));
                    }
                  }}
                />
              </th>
              <th className={styles.siteId}>
                {t('setting_operator_name.site_id')}
              </th>
              <th className={styles.category}>
                {t('setting_operator_name.item_category')}
              </th>
              <th className={styles.name}>
                {t('setting_operator_name.item_name')}
              </th>
              <th className={styles.operatorItem}>
                {t('setting_operator_name.operator_item')}
              </th>
            </tr>
          </thead>
          <tbody>
            {items && items.length > 0 ? (
              items.map((item, i) => {
                return (
                  <tr key={i}>
                    <td className={styles.checkbox}>
                      <CustomCheckBoxV2
                        id={item.id.toString()}
                        checked={selectedItemIds.includes(item.id)}
                        onChange={() => {
                          if (selectedItemIds.includes(item.id)) {
                            setSelectedItemIds(
                              selectedItemIds.filter((id) => id !== item.id)
                            );
                          } else {
                            setSelectedItemIds([...selectedItemIds, item.id]);
                          }
                        }}
                      />
                    </td>
                    <td className={styles.siteId}>
                      <p title={item.siteId}>{item.siteId}</p>
                    </td>
                    <td className={styles.category}>
                      <p title={item.categoryName}>{item.categoryName}</p>
                    </td>
                    <td className={styles.name}>
                      <div>
                        <span title={item.name} className={styles.nameText}>
                          {item.name}
                        </span>
                        {!item.operatorItem && (
                          <span className={styles.newLabel}>New</span>
                        )}
                      </div>
                    </td>
                    <td className={styles.operatorItem}>
                      <p title={item.operatorItem?.name || '-'}>
                        {item.operatorItem?.name || '-'}
                      </p>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td className="msg-error">{t('messages.M_008')}</td>
              </tr>
            )}
          </tbody>
          {items && items.length > 0 && (
            <TableLoading
              onNext={() => {
                if (items.length >= PAGE_SIZE && items.length < totalItems) {
                  setQuery((prev) => ({
                    ...prev,
                    page: prev.page + 1,
                  }));
                }
              }}
            />
          )}
        </table>
      </div>
    </div>
  );
};

export default AdminOperatorItemsTable;
